import React, { type ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import TooltipItem from 'atom-components/tooltip-item';
import BaseMarkerBadge from 'feature-components/badges/marker-badges/base-marker-badge';
import { markersOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import type { AuthorisableMark } from 'policies/entities/mark';
import MarkPolicy from 'policies/mark.policy';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { MarkType } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useAuthCustomer, useAuthCustomerUser } from 'services/providers/auth.hooks';
import { usePageAccess } from 'services/providers/page-access.provider';
import { isInitialLoad } from 'utils/check-query-status';

type ActiveMarkerBadgeProps = {
  authorisableMark: AuthorisableMark;
  isActiveMarker: boolean;
  activeMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
};

export default function ActiveMarkerBadge({
  authorisableMark,
  isActiveMarker,
  activeMarker
}: ActiveMarkerBadgeProps): ReactElement {
  const { authorisableUser } = usePageAccess();
  const { responseId } = useParams();
  const currentOrganisation = useAuthCustomer();
  const currentOrganisationUser = useAuthCustomerUser();

  // Get markers of the selected response.
  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      markersOfResponseParams(Number(responseId))
    );

  const organisationUserIds = useMemo(
    () => [...new Set(testInstanceResponseMarkersQuery.data?.json.map((marker) => marker.user.id))],
    [testInstanceResponseMarkersQuery.data?.json]
  );

  // Get all organisation users who are markers of this response only.
  const organisationUsersQuery = useCustomerUsers(
    {
      filters: {
        id: organisationUserIds,
        customer: currentOrganisation?.id
      },
      groups: ['id']
    },
    { enabled: !!organisationUserIds.length }
  );

  const isLoading = isInitialLoad(testInstanceResponseMarkersQuery, organisationUsersQuery);

  const canAdministrate = MarkPolicy.administrate(authorisableUser, authorisableMark);
  const canMarkIndependently = MarkPolicy.markIndependently(authorisableUser, authorisableMark);
  const canOnlyMarkIndependently = !canAdministrate && canMarkIndependently;

  const notActiveMarkerNote =
    canAdministrate || canMarkIndependently
      ? 'as you are an admin or teacher, you will be made the active marker when the form is saved.'
      : 'you must be set as the active marker (by a marking admin) to make changes.';
  const humanMarkerTooltip = isActiveMarker
    ? 'You are the active marker of this mark - you can make changes.'
    : `You are not the active marker of this mark - ${notActiveMarkerNote}`;

  /**
   * Find the active marker's organisation user if they exist, or set the current organisation user
   * if there is no existing active marker and the current user can only mark independently.
   *
   * We should only assume that the current unallocated organisation user will become the active
   * marker if they are an independent marker only since they will be automatically allocated on
   * save without this being explicitly requested/confirmed. Whereas a marking allocator should see
   * the red error badge if they are not allocated and there is no active marker set since they will
   * be explicitly asked to confirm that they should be set as the active marker on save.
   */
  const organisationUser: undefined | BaseCustomerUser =
    !activeMarker && authorisableMark.type === MarkType.Manual && canOnlyMarkIndependently
      ? organisationUsersQuery.data?.json.find(
          (orgUser) => orgUser.id === currentOrganisationUser?.id
        )
      : organisationUsersQuery.data?.json.find((orgUser) => orgUser.id === activeMarker?.user.id);

  // We should display the Active Marker badge, regardless of whether there is an associated
  // organisation user (error details are displayed inside the badge).
  return (
    <>
      {isLoading && 'Loading Active Marker...'}
      {!isLoading && (
        <TooltipItem
          tooltip={
            authorisableMark.type === MarkType.Autoscored
              ? "Autoscored - you can't make changes"
              : humanMarkerTooltip
          }
        >
          <BaseMarkerBadge
            displayWarning={!isActiveMarker && !canOnlyMarkIndependently}
            activeMarker={activeMarker}
            organisationUser={organisationUser}
            defaultMarkerToOrganisationUser={canOnlyMarkIndependently}
            autoscored={authorisableMark.type === MarkType.Autoscored}
            omitTooltip // Since we are setting a custom tooltip we can omit the default tooltip.
          />
        </TooltipItem>
      )}
    </>
  );
}
